import type { PublicAccount } from '@postpilot/core/domain';
import { motion } from 'framer-motion';
import { AlertCircle, Check, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/primitives';
import { cn } from '@/lib/utils';

/**
 * Picks which accounts a post goes to.
 *
 * Multi-select by default, because bulk posting is the whole point of the
 * product — a single-select that you have to hunt for a "post to more" toggle in
 * would bury the main feature.
 *
 * Accounts that need reconnecting are shown but not selectable. Hiding them
 * would be worse: the user would wonder where their account went. Showing them
 * as *disabled with a reason* tells them exactly what to do.
 */
export function AccountSelector({
  accounts,
  selectedIds,
  onToggle,
  onSelectAll,
  onClear,
  onConnect,
  error,
}: {
  accounts: PublicAccount[];
  selectedIds: string[];
  onToggle: (id: string) => void;
  onSelectAll: () => void;
  onClear: () => void;
  onConnect: () => void;
  error?: string;
}) {
  const active = accounts.filter((a) => a.status === 'active');
  const allSelected = active.length > 0 && selectedIds.length === active.length;

  if (accounts.length === 0) {
    return (
      <div className="rounded-lg border border-dashed p-6 text-center">
        <Users className="text-muted-foreground mx-auto size-5" />
        <p className="mt-2 text-sm font-medium">No accounts connected</p>
        <p className="text-muted-foreground mt-1 text-xs">
          Connect an X account before you can post.
        </p>
        <Button size="sm" variant="outline" className="mt-4" onClick={onConnect}>
          Connect X account
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium">
          Post to
          {selectedIds.length > 0 && (
            <span className="text-muted-foreground ml-1.5 text-xs font-normal">
              {selectedIds.length} selected
            </span>
          )}
        </label>

        {active.length > 1 && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-muted-foreground h-6 text-xs"
            onClick={allSelected ? onClear : onSelectAll}
          >
            {allSelected ? 'Clear' : 'Select all'}
          </Button>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        {accounts.map((account) => (
          <AccountChip
            key={account.id}
            account={account}
            selected={selectedIds.includes(account.id)}
            onToggle={() => onToggle(account.id)}
          />
        ))}
      </div>

      {error && (
        <p className="text-destructive text-xs" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

function AccountChip({
  account,
  selected,
  onToggle,
}: {
  account: PublicAccount;
  selected: boolean;
  onToggle: () => void;
}) {
  const isBroken = account.status !== 'active';

  const chip = (
    <motion.button
      type="button"
      onClick={isBroken ? undefined : onToggle}
      disabled={isBroken}
      whileTap={isBroken ? undefined : { scale: 0.97 }}
      aria-pressed={selected}
      className={cn(
        'flex items-center gap-2 rounded-full border py-1 pl-1 pr-3 text-sm transition-all',
        'focus-visible:ring-ring focus-visible:ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
        isBroken && 'cursor-not-allowed opacity-50',
        !isBroken && selected
          ? 'border-primary bg-primary text-primary-foreground shadow-sm'
          : !isBroken && 'border-input bg-background hover:border-primary/40 hover:bg-accent',
      )}
    >
      <Avatar className="size-6">
        <AvatarImage src={account.avatarUrl ?? undefined} alt="" />
        <AvatarFallback className="text-[9px]">
          {account.username.slice(0, 2).toUpperCase()}
        </AvatarFallback>
      </Avatar>

      <span className="font-medium">@{account.username}</span>

      {selected && !isBroken && <Check className="size-3.5" strokeWidth={3} />}
      {isBroken && <AlertCircle className="text-status-failed size-3.5" />}
    </motion.button>
  );

  if (isBroken) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          {/* A disabled button fires no pointer events, so the tooltip would
              never open. The span is what the tooltip actually hangs off. */}
          <span tabIndex={0}>{chip}</span>
        </TooltipTrigger>
        <TooltipContent>
          {account.lastError ?? 'This account needs reconnecting before you can post to it.'}
        </TooltipContent>
      </Tooltip>
    );
  }

  return chip;
}
